import React from 'react'
import {
  BanknotesIcon,
  ShieldCheckIcon,
  FingerPrintIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'

const ActionPanels = ({
  action,
  user,
  transactionLogs = [],
  balanceForm,
  onBalanceChange,
  onBalanceSubmit,
  balanceTypes = [],
  passwordForm,
  onPasswordChange,
  onPasswordReset,
  statusMessage,
  onDismissStatus,
  errorMessage,
  onClose,
  isMobileView = false,
}) => {
  if (!user) {
    return (
      <div className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-3xl border border-dashed border-customMuted/40 bg-white/70 p-6 text-center shadow-sm dark:border-white/10 dark:bg-customSurface text-customSecondaryText dark:text-customMuted">
        <FingerPrintIcon className="mb-4 h-10 w-10 text-customMuted" />
        Select a user and action to see more details.
      </div>
    )
  }

  const renderHeader = (icon, title, description) => (
    <div className="flex items-start gap-3">
      <span className="rounded-2xl bg-customSlate/80 p-2 text-customAccent dark:bg-white/5">
        {icon}
      </span>
      <div>
        <p className="text-lg font-semibold text-customInk dark:text-customHeading">
          {title}
        </p>
        <p className="text-sm text-customSecondaryText dark:text-customMuted">
          {description}
        </p>
      </div>
    </div>
  )

  const renderTransactionLog = () => (
    <div className="space-y-4">
      {renderHeader(
        <ShieldCheckIcon className="h-6 w-6" />,
        'Transaction Log',
        `Showing latest activity for ${user.username}`,
      )}

      {transactionLogs.length ? (
        <div className="overflow-hidden rounded-2xl border border-customSlate/60 bg-white/80 shadow dark:border-white/10 dark:bg-white/5">
          <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-white/5">
            <thead className="bg-background text-left text-xs font-semibold uppercase tracking-wide text-customLightMilkWhite dark:bg-white/5">
              <tr>
                <th className="px-4 py-3">Ref</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Channel</th>
                <th className="px-4 py-3">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 bg-white/80 dark:divide-white/5 dark:bg-transparent">
              {transactionLogs.map(item => (
                <tr key={item.id} className="text-xs">
                  <td className="px-4 py-3 font-semibold text-customInk dark:text-customHeading">
                    {item.id}
                  </td>
                  <td className="px-4 py-3 capitalize text-customSecondaryText dark:text-customMuted">
                    {item.type}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-customFontGreen">
                    {item.amount?.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </td>
                  <td className="px-4 py-3 text-customPrimaryText dark:text-customWhite">
                    {item.channel}
                  </td>
                  <td className="px-4 py-3 text-customMuted">
                    {new Date(item.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-customMuted/40 bg-white/80 p-6 text-center text-sm text-customSecondaryText dark:border-white/10 dark:bg-white/5 dark:text-customMuted">
          No transactions recorded for this user yet.
        </div>
      )}
    </div>
  )

  const renderBalancePanel = () => {
    const isDisabled =
      !balanceForm?.amount || Number(balanceForm?.amount) <= 0 || !balanceForm.type
    return (
      <div className="space-y-5">
        {renderHeader(
          <BanknotesIcon className="h-6 w-6" />,
          'Balance Adjustment',
          'Update wallet amount with a single action',
        )}
        <div className="space-y-3 text-sm">
          <label className="space-y-1">
            <span className="text-xs uppercase tracking-wide text-customMuted">
              Amount
            </span>
            <input
              type="number"
              min="0"
              value={balanceForm.amount}
              onChange={event => onBalanceChange('amount', event.target.value)}
              placeholder="Enter amount in MMK"
              className="w-full rounded-2xl border border-customMuted/40 bg-white/90 px-4 py-3 text-sm text-customPrimaryText shadow-sm focus:border-customAccent focus:outline-none focus:ring-2 focus:ring-customAccent/40 dark:border-white/10 dark:bg-white/5 dark:text-customWhite"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs uppercase tracking-wide text-customMuted">
              Transaction type
            </span>
            <select
              className="w-full rounded-2xl border border-customMuted/40 bg-white/90 px-4 py-3 text-sm text-customPrimaryText shadow-sm focus:border-customAccent focus:outline-none focus:ring-2 focus:ring-customAccent/40 dark:border-white/10 dark:bg-white/5 dark:text-customWhite"
              value={balanceForm.type}
              onChange={event => onBalanceChange('type', event.target.value)}
            >
              {balanceTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button
          type="button"
          disabled={isDisabled}
          onClick={onBalanceSubmit}
          className="w-full rounded-2xl bg-customAccent px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-customAccent/40 transition hover:-translate-y-0.5 hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-customMuted/40 disabled:text-customMuted"
        >
          Apply Update
        </button>
      </div>
    )
  }

  const renderPasswordPanel = () => {
    const hasError =
      passwordForm.password &&
      passwordForm.confirm &&
      passwordForm.password !== passwordForm.confirm
    const canSubmit =
      passwordForm.password &&
      passwordForm.confirm &&
      !hasError &&
      passwordForm.password.length >= 6

    return (
      <div className="space-y-5">
        {renderHeader(
          <FingerPrintIcon className="h-6 w-6" />,
          'Password Reset',
          'Create a secure temporary password for the user',
        )}
        <div className="space-y-3 text-sm">
          <label className="space-y-1">
            <span className="text-xs uppercase tracking-wide text-customMuted">
              New password
            </span>
            <input
              type="password"
              value={passwordForm.password}
              name="new-password"
              autoComplete="new-password"
              onChange={event => onPasswordChange('password', event.target.value)}
              placeholder="Enter at least 6 characters"
              className="w-full rounded-2xl border border-customMuted/40 bg-white/90 px-4 py-3 text-sm text-customPrimaryText shadow-sm focus:border-customAccent focus:outline-none focus:ring-2 focus:ring-customAccent/40 dark:border-white/10 dark:bg-white/5 dark:text-customWhite"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs uppercase tracking-wide text-customMuted">
              Confirm password
            </span>
            <input
              type="password"
              value={passwordForm.confirm}
              name="confirm-password"
              autoComplete="new-password"
              onChange={event => onPasswordChange('confirm', event.target.value)}
              placeholder="Repeat the password"
              className="w-full rounded-2xl border border-customMuted/40 bg-white/90 px-4 py-3 text-sm text-customPrimaryText shadow-sm focus:border-customAccent focus:outline-none focus:ring-2 focus:ring-customAccent/40 dark:border-white/10 dark:bg-white/5 dark:text-customWhite"
            />
          </label>
          {hasError ? (
            <p className="text-xs text-rose-500">Passwords do not match.</p>
          ) : null}
        </div>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={onPasswordReset}
          className="w-full rounded-2xl bg-customInk px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-customInk/20 transition hover:-translate-y-0.5 hover:bg-indigo-950 disabled:cursor-not-allowed disabled:bg-customMuted/40 disabled:text-customMuted"
        >
          Reset Password
        </button>
      </div>
    )
  }

  const renderInfoPanel = () => {
    const sharePercent = user.sharePercent ?? null
    const betLimitSingle = user.betLimitForSingle ?? null
    const betLimitMix = user.betLimitForMix ?? null
    const isShareEnabled =
      typeof user.isShare === 'boolean'
        ? user.isShare
        : user.shareEnabled ?? false
    const isLocked =
      typeof user.lock === 'boolean'
        ? user.lock
        : user.isLocked ?? (user.status?.toLowerCase() === 'locked')
    const lastLoginRaw = user.lastLoginDate ?? null

    const formatLimit = value => {
      if (value === undefined || value === null) return 'Not set'
      const numeric = Number(value)
      if (Number.isNaN(numeric)) return value
      return numeric.toLocaleString()
    }

    const sharePercentDisplay =
      sharePercent === undefined || sharePercent === null
        ? 'Not set'
        : Number.isNaN(Number(sharePercent))
          ? sharePercent
          : `${Number(sharePercent)}%`

    const formattedLastLogin = lastLoginRaw !== "0001-01-01T00:00:00"
      ? new Date(lastLoginRaw).toLocaleString()
      : 'No login recorded'

    return (
      <div className="space-y-5">
        {renderHeader(
          <InformationCircleIcon className="h-6 w-6" />,
          'User Information',
          'View detailed information about the selected user',
        )}

        <div className="rounded-3xl border border-customSlate/60 bg-white/80 p-5 shadow-lg dark:border-white/10 dark:bg-white/5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl bg-gradient-to-br from-customSlate/20 via-white to-white p-4 shadow-xl dark:from-white/5 dark:via-transparent dark:to-transparent">
              <p className="text-xs uppercase tracking-wide text-customSecondaryText dark:text-customMuted">Share Access</p>
              <p className="mt-1 text-2xl font-semibold text-customInk dark:text-customHeading">
                {isShareEnabled ? 'Enabled' : 'Disabled'}
              </p>
              <p className="mt-3 rounded-2xl border border-white/60 bg-customSky/50 px-3 py-2 text-xs font-medium text-customSecondaryText shadow-lg dark:border-white/10 dark:bg-white/5 dark:text-customMuted">
                Share Percent: <span className="font-semibold text-customInk dark:text-customHeading">{sharePercentDisplay}</span>
              </p>
            </div>
            <div className="rounded-2xl bg-customSlate/10 p-4 shadow-xl dark:bg-white/5">
              <p className="text-xs uppercase tracking-wide text-customSecondaryText dark:text-customMuted">Account Lock</p>
              <p className={`mt-1 text-2xl font-semibold ${isLocked ? 'text-customFontRed' : 'text-customFontLightGreen'}`}>
                {isLocked ? 'Locked' : 'Active'}
              </p>
              <div className="mt-4 space-y-1 text-xs text-customSecondaryText dark:text-customMuted">
                <p>Last login</p>
                <p className="text-sm font-medium text-customInk dark:text-customHeading">
                  {formattedLastLogin}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 grid gap-4 text-sm sm:grid-cols-2">
            <div className="rounded-2xl border border-white/60 bg-white/80 p-4 text-customSecondaryText shadow-xl dark:border-white/10 dark:bg-white/5">
              <p className="text-xs uppercase tracking-wide dark:text-customMuted">Bet Limit (Single)</p>
              <p className="mt-2 text-lg font-semibold text-customInk dark:text-customHeading">
                {formatLimit(betLimitSingle)}
              </p>
              <p className="text-xs dark:text-customMuted">Maximum allowed per single slip</p>
            </div>
            <div className="rounded-2xl border border-white/60 bg-white/80 p-4 text-customSecondaryText shadow-xl dark:border-white/10 dark:bg-white/5">
              <p className="text-xs uppercase tracking-wide dark:text-customMuted">Bet Limit (Mix)</p>
              <p className="mt-2 text-lg font-semibold text-customInk dark:text-customHeading">
                {formatLimit(betLimitMix)}
              </p>
              <p className="text-xs dark:text-customMuted">Maximum allowed per mix slip</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderContent = () => {
    if (action === 'logs') return renderTransactionLog()
    if (action === 'balance') return renderBalancePanel()
    if (action === 'password') return renderPasswordPanel()
    if (action === 'profile') return renderInfoPanel()

    return (
      <div className="rounded-3xl border border-dashed border-customMuted/40 bg-white/70 p-6 text-center text-sm text-customSecondaryText shadow-sm dark:border-white/10 dark:bg-customSurface dark:text-customMuted">
        Choose Transaction Log, Balance, or Password actions to see more
        details.
      </div>
    )
  }

  const containerClasses = `flex flex-col gap-4 rounded-3xl border border-white/70 bg-white/90 p-5 shadow-sm shadow-customSlate/40 backdrop-blur dark:border-white/10 dark:bg-custom_dark_secondary/40 ${isMobileView ? 'max-h-[85vh] overflow-y-auto' : ''
    }`

  return (
    <div className={containerClasses}>
      {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="absolute top-5 right-5 rounded-full border border-customMuted/30 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-customInk transition hover:bg-customSlate/60 dark:border-white/20 dark:text-customHeading dark:hover:bg-customSurfaceMuted/60"
            >
              Close
            </button>
          ) : null}
      <div className={`flex items-start ${isMobileView ? 'flex-col' : 'justify-between' } gap-3`}>
        <div>
          <p className="text-xs uppercase tracking-wide text-customSecondaryText dark:text-customMuted">
            Selected user
          </p>
          <p className="text-lg font-semibold text-customInk dark:text-customHeading">
            {user.username}
          </p>
          <p className="text-sm text-customSecondaryText dark:text-customMuted">
            {user.role} · {user.id}
          </p>
        </div>
        <div className={`flex ${isMobileView ? 'flex-col' : 'items-start'} gap-2`}>
          <div className={`${isMobileView ? 'text-start' : 'text-right'}`}>
            <p className="text-xs uppercase tracking-wide text-customSecondaryText dark:text-customMuted">
              Balance
            </p>
            <p className="text-2xl font-bold text-customFontGreen">
              {user.balance?.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}
            </p>
          </div>
        </div>
      </div>

      {statusMessage ? (
        <div className="flex items-start justify-between rounded-2xl border border-customVibrantGreen/40 bg-customVibrantGreen/10 px-4 py-3 text-sm text-customVibrantGreen dark:border-customVibrantGreen/30 dark:bg-customVibrantGreen/20 dark:text-customHeading">
          <p>{statusMessage}</p>
          <button
            type="button"
            onClick={onDismissStatus}
            className="text-xs font-semibold uppercase tracking-wide text-customVibrantGreen"
          >
            Dismiss
          </button>
        </div>
      ) : null}

       {errorMessage ? (
        <div className="flex items-start justify-between rounded-2xl border border-customFontRed/40 bg-customFontRed/10 px-4 py-3 text-sm text-customFontRed dark:border-customFontRed/30 dark:bg-customFontRed/20 dark:text-customHeading">
          <p>{errorMessage}</p>
          <button
            type="button"
            onClick={onDismissStatus}
            className="text-xs font-semibold uppercase tracking-wide text-customFontRed"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {renderContent()}
    </div>
  )
}

export default ActionPanels
